import { useEffect, useState } from 'react';

const NEAR_VIEW_MARGIN = '200px 0px';
// Zero-sized / hidden wrappers (collapsed editors, modals) never intersect —
// mount after a beat so the video is never stuck behind the skeleton.
const MOUNT_FALLBACK_MS = 1200;

/**
 * Lazy-mount + autoplay management for a <video> element.
 *
 * - `shouldMount` turns true when the wrapper nears the viewport (or after a
 *   short fallback), so a page full of media doesn't download on first paint.
 * - While mounted, the video plays when visible and pauses when scrolled away.
 *
 * @param {React.RefObject} wrapperRef — the element observed for visibility
 * @param {React.RefObject} videoRef   — the <video> to play/pause
 * @param {string} src                 — re-observes when the source changes
 * @returns {boolean} shouldMount
 */
export default function useVideoInView(wrapperRef, videoRef, src) {
    const [shouldMount, setShouldMount] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setShouldMount(true), MOUNT_FALLBACK_MS);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el || !('IntersectionObserver' in window)) {
            setShouldMount(true);
            return;
        }

        const onVisibilityChange = ([entry]) => {
            if (entry.isIntersecting) setShouldMount(true);

            const videoEl = videoRef.current;
            if (!videoEl) return;
            if (entry.isIntersecting) {
                videoEl.play()?.catch((error) => console.warn('Autoplay prevented:', error));
            } else {
                videoEl.pause();
            }
        };

        const observer = new IntersectionObserver(onVisibilityChange, { rootMargin: NEAR_VIEW_MARGIN });
        observer.observe(el);
        return () => observer.disconnect();
    }, [wrapperRef, videoRef, src]);

    return shouldMount;
}
